/**
 * Buttons for the OneStamp dashboard.
 *
 * Variants:
 *   primary   — solid gold (default CTA)
 *   secondary — outline gold
 *   subtle    — neutral surface
 *   ghost     — text-only
 *   danger    — destructive red
 *
 * Sizes: sm | md | lg
 *
 * Use `loading` to show a spinner and auto-disable.
 */
import { classes } from '@/lib/utils';

const VARIANT = {
  primary:
    'bg-gold text-black hover:brightness-110 active:brightness-95 border border-gold shadow-glow-gold-sm hover:shadow-glow-gold disabled:opacity-50 disabled:shadow-none',
  secondary:
    'bg-transparent text-gold hover:bg-gold/10 border border-gold/40 hover:shadow-glow-gold-sm disabled:opacity-50',
  subtle:
    'bg-white/5 text-white hover:bg-white/10 border border-white/10 disabled:opacity-50',
  ghost:
    'bg-transparent text-white/70 hover:text-white hover:bg-white/5 border border-transparent disabled:opacity-50',
  danger:
    'bg-red-500/15 text-red-300 hover:bg-red-500/25 border border-red-500/30 disabled:opacity-50',
};

const SIZE = {
  sm: 'px-3 py-1.5 text-xs rounded-2xl',
  md: 'px-4 py-2 text-sm rounded-2xl',
  lg: 'px-5 py-3 text-sm rounded-2xl',
};

export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  type = 'button',
  className,
  leftIcon,
  rightIcon,
  testID,
  ...rest
}) {
  const isDisabled = disabled || loading;
  return (
    <button
      type={type}
      disabled={isDisabled}
      data-testid={testID}
      className={classes(
        'inline-flex items-center justify-center gap-2 font-medium transition-all',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-gold/40',
        'disabled:cursor-not-allowed',
        VARIANT[variant],
        SIZE[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
      ) : leftIcon ? (
        <span className="-ml-0.5">{leftIcon}</span>
      ) : null}
      <span>{children}</span>
      {!loading && rightIcon ? <span className="-mr-0.5">{rightIcon}</span> : null}
    </button>
  );
}
